import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Resource } from "../../core/models/wrappers/Resource";
import { useApiClient } from "../clients/ApiClient";
import { PageQuery } from "../../utils/models/pageQuery";
import { PagedResponse } from "../models/responses/pagedResponse";
import { PrepaymentVendorDto } from "../models/responses/PrepaymentVendorDto";
import { SortDirection } from "../../presentation/components/GenericTable/Models/sortDirection";

/**
 * The complete vendor list, assembled client-side by walking every server
 * page. Shaped as a PagedResponse on purpose: the setForcePrepayment cache
 * patch maps over `results` on every ["prepayments", "list"] query, so this
 * snapshot stays consistent with the paged lists after a toggle.
 */
export interface PrepaymentVendorsSnapshot
  extends PagedResponse<PrepaymentVendorDto> {
  /**
   * True when every server page was fetched. False only when the safety cap
   * stopped the loop early (unexpectedly large list) — the UI must then say
   * the filtered view is partial instead of presenting it as the full truth.
   */
  complete: boolean;
}

export interface PrepaymentsRepository {
  useVendors(
    query: PageQuery,
    options?: { enabled?: boolean },
  ): Resource<PagedResponse<PrepaymentVendorDto>>;
  /**
   * Fetches ALL vendor pages for a search term and concatenates them. The
   * API has no source filter, so the admin screen's source chips filter
   * client-side — which is only honest when the whole list is in memory.
   * The vendor list is small (typically a few hundred rows), so this is a
   * handful of requests at most.
   */
  useAllVendors(
    searchTerm: string,
    enabled: boolean,
  ): Resource<PrepaymentVendorsSnapshot>;
  /**
   * PUT prepayments/vendors/{orgId} with { forcePrepayment }. The server
   * responds with the updated row, which is written straight into every
   * cached vendor list so the toggle reflects the confirmed state without a
   * full refetch; a background invalidation keeps other pages honest.
   */
  setForcePrepayment(
    orgId: number,
    forcePrepayment: boolean,
  ): Promise<PrepaymentVendorDto>;
}

const PREPAYMENTS_ROOT_KEY = "prepayments";

// Page size / request cap for the fetch-all loop. 100 × 30 covers 3.000
// vendors — far beyond the real list — while guaranteeing termination.
const FETCH_ALL_PAGE_SIZE = 100;
const FETCH_ALL_MAX_REQUESTS = 30;

export function usePrepaymentsRepository(): PrepaymentsRepository {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  function useVendors(
    query: PageQuery,
    options?: { enabled?: boolean },
  ): Resource<PagedResponse<PrepaymentVendorDto>> {
    return useQuery(
      [PREPAYMENTS_ROOT_KEY, "list", query],
      () => apiClient.getPrepaymentVendors(query),
      {
        // Admin surface — always show the current server state on entry, and
        // keep the previous page visible while the next one loads.
        staleTime: 0,
        keepPreviousData: true,
        enabled: options?.enabled ?? true,
      },
    );
  }

  async function fetchAllVendors(
    searchTerm: string,
  ): Promise<PrepaymentVendorsSnapshot> {
    const seen = new Set<number>();
    const results: PrepaymentVendorDto[] = [];
    let pageIndex = 1;
    let pageCount = 1;
    let totalCount: number | undefined;
    let requests = 0;

    do {
      const page = await apiClient.getPrepaymentVendors({
        searchTerm,
        filterBy: [],
        sortBy: { field: "name", sortDirection: SortDirection.ASC },
        page: { size: FETCH_ALL_PAGE_SIZE, index: pageIndex },
      });
      requests += 1;
      // The loop is driven by the server's own pageCount, so it stays correct
      // even if the server caps the requested page size.
      pageCount = page.pageCount ?? 1;
      totalCount = page.totalCount ?? totalCount;
      if (page.results.length === 0) {
        // No more rows — treat as the end of the list (also covers an empty
        // search result and guards against looping on odd responses).
        pageIndex = pageCount + 1;
        break;
      }
      for (const vendor of page.results) {
        // Dedupe defensively: rows can shift between pages if the list
        // changes while the loop runs.
        if (seen.has(vendor.organizationId)) continue;
        seen.add(vendor.organizationId);
        results.push(vendor);
      }
      pageIndex += 1;
    } while (pageIndex <= pageCount && requests < FETCH_ALL_MAX_REQUESTS);

    return {
      results,
      pageIndex: 1,
      pageSize: results.length,
      pageCount: 1,
      totalCount: totalCount ?? results.length,
      complete: pageIndex > pageCount,
    };
  }

  function useAllVendors(
    searchTerm: string,
    enabled: boolean,
  ): Resource<PrepaymentVendorsSnapshot> {
    return useQuery(
      // Keyed under [root, "list"] on purpose — see PrepaymentVendorsSnapshot.
      [PREPAYMENTS_ROOT_KEY, "list", "all", searchTerm],
      () => fetchAllVendors(searchTerm),
      {
        staleTime: 0,
        keepPreviousData: true,
        enabled,
      },
    );
  }

  async function setForcePrepayment(
    orgId: number,
    forcePrepayment: boolean,
  ): Promise<PrepaymentVendorDto> {
    const updated = await apiClient.setPrepaymentVendor(orgId, forcePrepayment);

    // A fetch-all snapshot refetch that was in flight when the PUT landed
    // would resolve with pre-toggle rows and overwrite the patch below (the
    // snapshot holds EVERY row, so the toggled vendor is always in it —
    // unlike a paged fetch). Cancel it first: react-query reverts to the
    // cached snapshot without erroring, and the patch then writes the
    // server-confirmed row into it.
    await queryClient.cancelQueries({
      queryKey: [PREPAYMENTS_ROOT_KEY, "list", "all"],
    });

    // Server-confirmed update: patch the row into every cached list page.
    queryClient.setQueriesData<PagedResponse<PrepaymentVendorDto> | undefined>(
      { queryKey: [PREPAYMENTS_ROOT_KEY, "list"] },
      (previous) =>
        previous === undefined
          ? previous
          : {
              ...previous,
              results: previous.results.map((vendor) =>
                vendor.organizationId === updated.organizationId
                  ? updated
                  : vendor,
              ),
            },
    );
    await queryClient.invalidateQueries({
      queryKey: [PREPAYMENTS_ROOT_KEY],
      refetchType: "none",
    });

    return updated;
  }

  return {
    useVendors,
    useAllVendors,
    setForcePrepayment,
  };
}
